import {createContext, ReactNode, useContext, useReducer, useRef} from 'react';
import helloPNG from '../assets/hello_anim.png';
import thinkingPNG from '../assets/think.png';
import dogPNG from '../assets/dog.png';
import restaurantPNG from '../assets/food.png';
import winterPNG from '../assets/umbrella.png';
import waterPNG from '../assets/water.png';

type AnimationState = {
  isOpen: boolean;
  show: () => void;
  hide: () => void;
  image: string;
}

type AnimationsContextType = {
  animationDuration: number;
  animations: {
    hello: AnimationState;
    thinking: AnimationState;
    dog: AnimationState;
    restaurant: AnimationState;
    winter: AnimationState;
    water: AnimationState;
  };
  handleUserInputAnimations: (input: string) => void;
  handleStartThinkingAnimation: () => void;
  handleStopThinkingAnimation: () => void;
}

type ReducerState = {
  hello: boolean;
  thinking: boolean;
  dog: boolean;
  restaurant: boolean;
  winter: boolean;
  water: boolean;
}

type ReducerAction = {
  type: 'hideHelloAnimation' | 'showHelloAnimation'
    | 'hideThinkingAnimation' | 'showThinkingAnimation'
    | 'hideDogAnimation' | 'showDogAnimation'
    | 'hideRestaurantAnimation' | 'showRestaurantAnimation'
    | 'hideWinterAnimation' | 'showWinterAnimation'
    | 'hideWaterAnimation' | 'showWaterAnimation';
  payload?: unknown;
}

const initialState = {
  hello: false,
  thinking: false,
  dog: false,
  restaurant: false,
  winter: false,
  water: false,
};

interface AnimationsContextProviderProps {
  children: ReactNode;
}

const animationDuration = 4000;
const animationThinkingDelay = 5000;

const dogText = 'כלב';
const restaurantTexts = ['מסעדה', 'מסעדות', 'אוכל', 'לאכול'];
const winterTexts = ['גשם', 'חורף'];
const waterTexts = [
  'טיול מים',
  'טיולי מים',
  'טיול במים',
  'טיול עם מים',
  'מסלול מים',
  'מסלולי מים',
  'מסלול במים',
  'מסלול עם מים'
];

export const AnimationsContext = createContext<AnimationsContextType>({
  animationDuration,
  animations: {
    hello: {
      image: helloPNG,
      isOpen: false,
      show: () => {},
      hide: () => {},
    },
    thinking: {
      image: thinkingPNG,
      isOpen: false,
      show: () => {},
      hide: () => {},
    },
    dog: {
      image: dogPNG,
      isOpen: false,
      show: () => {},
      hide: () => {},
    },
    restaurant: {
      image: restaurantPNG,
      isOpen: false,
      show: () => {},
      hide: () => {},
    },
    winter: {
      image: winterPNG,
      isOpen: false,
      show: () => {},
      hide: () => {},
    },
    water: {
      image: waterPNG,
      isOpen: false,
      show: () => {},
      hide: () => {},
    },
  },
  handleUserInputAnimations: () => {},
  handleStartThinkingAnimation: () => {},
  handleStopThinkingAnimation: () => {},
});

export const useAnimationsContext = () => useContext(AnimationsContext);

export const AnimationsContextProvider = ({children}: AnimationsContextProviderProps) => {
  const [state, dispatch] = useReducer(
    (state: ReducerState, action: ReducerAction) => {
      switch (action.type) {
        case 'hideHelloAnimation':
          return {...state, hello: false};
        case 'showHelloAnimation':
          return {...state, hello: true};
        case 'hideThinkingAnimation':
          return {...state, thinking: false};
        case 'showThinkingAnimation':
          return {...state, thinking: true};
        case 'hideDogAnimation':
          return {...state, dog: false};
        case 'showDogAnimation':
          return {...state, dog: true};
        case 'hideRestaurantAnimation':
          return {...state, restaurant: false};
        case 'showRestaurantAnimation':
          return {...state, restaurant: true};
        case 'hideWinterAnimation':
          return {...state, winter: false};
        case 'showWinterAnimation':
          return {...state, winter: true};
        case 'hideWaterAnimation':
          return {...state, water: false};
        case 'showWaterAnimation':
          return {...state, water: true};
        default:
          return state;
      }
    },
    initialState
  );
  const animations = {
    hello: {
      image: helloPNG,
      isOpen: state.hello,
      show: () => dispatch({type: 'showHelloAnimation'}),
      hide: () => dispatch({type: 'hideHelloAnimation'}),
    },
    thinking: {
      image: thinkingPNG,
      isOpen: state.thinking,
      show: () => dispatch({type: 'showThinkingAnimation'}),
      hide: () => dispatch({type: 'hideThinkingAnimation'}),
    },
    dog: {
      image: dogPNG,
      isOpen: state.dog,
      show: () => dispatch({type: 'showDogAnimation'}),
      hide: () => dispatch({type: 'hideDogAnimation'}),
    },
    restaurant: {
      image: restaurantPNG,
      isOpen: state.restaurant,
      show: () => dispatch({type: 'showRestaurantAnimation'}),
      hide: () => dispatch({type: 'hideRestaurantAnimation'}),
    },
    winter: {
      image: winterPNG,
      isOpen: state.winter,
      show: () => dispatch({type: 'showWinterAnimation'}),
      hide: () => dispatch({type: 'hideWinterAnimation'}),
    },
    water: {
      image: waterPNG,
      isOpen: state.water,
      show: () => dispatch({type: 'showWaterAnimation'}),
      hide: () => dispatch({type: 'hideWaterAnimation'}),
    },
  };

  const thinkingTimer = useRef<NodeJS.Timeout | null>(null);
  const handleStartThinkingAnimation = () => {
    if (
      !state.hello
      || !state.dog
      || !state.restaurant
      || !state.winter
      || !state.water
    ) {
      thinkingTimer.current = setTimeout(() => dispatch({type: 'showThinkingAnimation'}), animationThinkingDelay);
    }
  };
  const handleStopThinkingAnimation = () => {
    dispatch({type: 'hideThinkingAnimation'});

    if (thinkingTimer.current) {
      clearTimeout(thinkingTimer.current);
    }
    thinkingTimer.current = null;
  };

  const handleUserInputAnimations = (userInput: string) => {
    if (userInput.match(new RegExp(dogText, 'i'))) {
      animations.dog.show();
    }
    if (restaurantTexts.map(restaurantText => userInput.match(new RegExp(restaurantText, 'i'))).some(Boolean)) {
      animations.restaurant.show();
    }
    if (winterTexts.map(winterText => userInput.match(new RegExp(winterText, 'i'))).some(Boolean)) {
      animations.winter.show();
    }
    if (waterTexts.map(waterText => userInput.match(new RegExp(waterText, 'i'))).some(Boolean)) {
      animations.water.show();
    }
  };

  return (
    <AnimationsContext.Provider value={{
      animationDuration,
      animations,
      handleUserInputAnimations,
      handleStartThinkingAnimation,
      handleStopThinkingAnimation
    }}>
      {children}
    </AnimationsContext.Provider>
  );
};